import React from 'react';
import { useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { conversationService, type Conversation, type Message } from '@/services/conversationService';
import { useAuth } from '@/contexts/AuthContext';
import toast from 'react-hot-toast';

export type { Conversation, Message };

export function useConversations() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const queryKey = ['conversations', user?.id];

    const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading: loading, error } = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam = 0 }) => conversationService.fetchAll(pageParam as number, 50),
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => lastPage.length === 50 ? allPages.length * 50 : undefined,
        enabled: !!user,
    });

    const conversations = React.useMemo(() => data ? data.pages.flat() : [], [data]);

    const createMutation = useMutation({
        mutationFn: ({ personaId, moodId, contextId, context, collectionId, tagIds }: { personaId: string, moodId: string, contextId?: string, context?: string, collectionId?: string | null, tagIds?: string[] }) =>
            conversationService.create(personaId, moodId, contextId, context, collectionId, tagIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
        onError: () => {
            toast.error('Erro ao iniciar conversa.');
        }
    });

    const addMessageMutation = useMutation({
        mutationFn: ({ conversationId, role, content }: { conversationId: string, role: 'user' | 'assistant', content: string }) =>
            conversationService.addMessage(conversationId, role, content),
        onSuccess: (data, variables) => {
            // Optimistic update or just invalidate
            queryClient.setQueryData(queryKey, (oldData: InfiniteData<Conversation[]> | undefined) => {
                if (!oldData) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page: Conversation[]) =>
                        page.map(c => {
                            if (c.id === variables.conversationId) {
                                const newMessage = {
                                    id: data.id,
                                    role: variables.role,
                                    content: variables.content,
                                    created_at: data.created_at
                                };
                                return { ...c, messages: [...c.messages, newMessage as Message] };
                            }
                            return c;
                        })
                    )
                };
            });
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, updates, tagIds }: { id: string, updates: Partial<Conversation>, tagIds?: string[] }) =>
            conversationService.update(id, updates, tagIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: conversationService.delete,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey });
            toast.success('Conversa excluída!');
        },
        onError: () => {
            toast.error('Erro ao excluir conversa.');
        }
    });

    return {
        conversations,
        loading,
        error,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        createConversation: (personaId: string, moodId: string, contextId?: string, context?: string, collectionId?: string | null, tagIds?: string[]) =>
            createMutation.mutateAsync({ personaId, moodId, contextId, context, collectionId, tagIds }),
        addMessage: (conversationId: string, role: 'user' | 'assistant', content: string) =>
            addMessageMutation.mutateAsync({ conversationId, role, content }),
        updateConversation: (id: string, updates: Partial<Conversation>, tagIds?: string[]) =>
            updateMutation.mutateAsync({ id, updates, tagIds }),
        deleteConversation: deleteMutation.mutateAsync,
        setConversations: (updater: InfiniteData<Conversation[]> | ((old: InfiniteData<Conversation[]> | undefined) => InfiniteData<Conversation[]> | undefined)) => {
            // For optimistic updates or manual overrides if absolutely needed,
            // but prefer React Query cache manipulation.
            // This is a placeholder to maintain API compatibility if needed.
            queryClient.setQueryData(queryKey, updater);
        }
    };
}
